/**
 * use-properties-panel-auto-open.ts — D-050-03b first-Select-mode-mint
 * auto-open trigger with sticky-close.
 *
 * Conditions for auto-open:
 *   (a) `currentProject?.aboxElementMap != null` (D-048-03 ABox-path gate)
 *   (b) `previewSelection` transitions from null → non-null
 *   (c) FIRST transition in the session (per-hook-instance `useRef<boolean>`
 *       sticky flag)
 *
 * Once the operator manually closes via `closeInspector()`, the hook
 * never re-opens automatically — the sticky flag stays `true` for the
 * remainder of the hook-instance lifetime (page-reload resets it via
 * React unmount).
 *
 * Mount this hook in `preview-surface.tsx` (the chrome host where the
 * Phase 049-03 chip + Discard-all already live) so the effect runs
 * whenever the Preview is visible. The internal gates (sticky-flag +
 * aboxElementMap) make it safe to mount outside the ABox conditional —
 * non-ABox paths fall through the second guard and never auto-open.
 *
 * Returns `void` — the hook only fires a side-effect.
 */
import { useEffect, useRef } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { usePreviewSelectionStore } from '@/features/preview/store/preview-selection-slice'

export function usePropertiesPanelAutoOpen(): void {
  const previewSelection = usePreviewSelectionStore(
    (s) => s.previewSelection,
  )
  const aboxElementMap = useStudioStore(
    (s) => s.currentProject?.aboxElementMap,
  )
  const hasAutoOpened = useRef<boolean>(false)

  useEffect(() => {
    if (hasAutoOpened.current) return
    if (!aboxElementMap) return
    if (!previewSelection) return

    hasAutoOpened.current = true
    useStudioStore.getState().openAboxPropertiesPanel()
  }, [previewSelection, aboxElementMap])
}
